import {Animated, Dimensions, Easing, Pressable, View} from "react-native"
import React, {useEffect, useRef} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import {digg} from "diggerize"
import Link from "@kaspernj/api-maker/build/link"
import memo from "set-state-compare/build/memo"
import {Portal} from "conjointment"
import {Text} from "shared/base"
import useEventListener from "ya-use-event-listener"
import {useModals} from "components/modal"
import usePressOutside from "outside-eye/build/use-press-outside"

/**
 * @typedef {object} ChalkyLayoutHoverMenuItemInnerProps
 * @property {unknown} label
 */

/** @typedef {object} ChalkyLayoutHoverMenuItemInnerState */

/**
 * @extends {ShapeComponent<ChalkyLayoutHoverMenuItemInnerProps, ChalkyLayoutHoverMenuItemInnerState>}
 */
class ChalkyLayoutHoverMenuItemInner extends ShapeComponent {
  render() {
    const {label, ...restProps} = this.props

    return (
      <Link {...restProps}>
        <Text
          style={this.textStyle ||= {
            marginTop: 10,
            marginBottom: 10,
            color: "#b7b8ff",
            fontSize: 14,
            whiteSpace: "nowrap"
          }}
        >
          {label}
        </Text>
      </Link>
    )
  }
}

const ChalkyLayoutHoverMenuItem = memo(shapeComponent(ChalkyLayoutHoverMenuItemInner))

/**
 * @typedef {object} ChalkyLayoutHoverMenuInnerProps
 * @property {unknown} children
 * @property {unknown} content
 */

/**
 * @typedef {object} ChalkyLayoutHoverMenuInnerState
 * @property {unknown} clientX
 * @property {unknown} clientY
 * @property {unknown} popupMenuPosition
 * @property {unknown} showMenu
 * @property {string} visibility
 */

/**
 * @extends {ShapeComponent<ChalkyLayoutHoverMenuInnerProps, ChalkyLayoutHoverMenuInnerState>}
 */
class ChalkyLayoutHoverMenuInner extends ShapeComponent {
  /** @type {ChalkyLayoutHoverMenuInnerState} */
  state = {
    clientX: undefined,
    clientY: undefined,
    popupMenuPosition: null,
    showMenu: undefined,
    visibility: "hidden"
  }

  hoverMenuHeight = null
  hoverMenuWidth = null

  initialized = false

  windowHeight = Dimensions.get("window").height
  windowWidth = Dimensions.get("window").width

  popupMenuLeft = new Animated.Value(0)
  popupMenuTop = new Animated.Value(0)

  zIndex = new Animated.Value(1000)

  setup() {
    const modals = useModals()
    const insideModal = Boolean(digg(modals, "active"))

    this.viewRef = useRef()

    useEffect(() => {
      this.zIndex.setValue(insideModal ? 10001 : 1000)
    }, [insideModal])

    useEventListener(Dimensions, "change", this.tt.onDimensionsChange)
    usePressOutside(this.tt.viewRef, this.tt.onClickOutsideHoverMenu)
  }

  closeMenu() {
    this.hoverMenuHeight = null
    this.hoverMenuWidth = null
    this.initialized = false
    this.setState({showMenu: false, visibility: "hidden"})
  }

  onClickOutsideHoverMenu = () => {
    if (this.s.showMenu) {
      setTimeout(() => this.closeMenu(), 0)
    } else {
      this.closeMenu()
    }
  }

  render() {
    const {children, content} = this.props
    const {visibility} = this.s

    return (
      <View testID="components/chalky-layout/hover-menu">
        <Pressable onMouseDown={this.tt.onMouseMove} onMouseMove={this.tt.onMouseMove} onPress={this.tt.onPress}>
          {children}
        </Pressable>
        {this.s.showMenu &&
          <Portal name="hover-menu">
            <Animated.View
              dataSet={this.hoverMenuViewDataSet ||= {class: "hover-menu"}}
              onLayout={this.tt.onHoverMenuLayout}
              ref={this.tt.viewRef}
              style={this.cache("hoverMenuStyle", {
                visibility,
                position: "absolute",
                zIndex: this.tt.zIndex,
                left: this.tt.popupMenuLeft,
                top: this.tt.popupMenuTop,
                padding: 12,
                backgroundColor: "#1b1f72",
                borderRadius: 16,
                boxShadow: "0px 8px 10px 0px rgba(0, 0, 0, 0.0706)"
              }, [visibility])}
            >
              {content()}
            </Animated.View>
          </Portal>
        }
      </View>
    )
  }

  positionHoverMenu(mode) {
    const {hoverMenuHeight, hoverMenuWidth, initialized, popupMenuClientX, popupMenuClientY, windowHeight, windowWidth} = this.tt
    const furthestX = popupMenuClientX + hoverMenuWidth
    const furthestY = popupMenuClientY + hoverMenuHeight
    let popupMenuLeft, popupMenuPositionX, popupMenuPositionY, popupMenuTop

    if (furthestX >= windowWidth) {
      popupMenuLeft = popupMenuClientX - hoverMenuWidth
      popupMenuPositionX = "left"
    } else {
      popupMenuLeft = popupMenuClientX
      popupMenuPositionX = "right"
    }

    if (furthestY >= windowHeight) {
      popupMenuTop = popupMenuClientY - hoverMenuHeight
      popupMenuPositionY = "top"
    } else {
      popupMenuTop = popupMenuClientY
      popupMenuPositionY = "below"
    }

    if (popupMenuPositionX != this.tt.popupMenuPositionX || !initialized) {
      this.popupMenuPositionX = popupMenuPositionX

      if (this.initialized) {
        Animated
          .timing(this.popupMenuLeft, {
            duration: 200,
            easing: Easing.inOut(Easing.linear),
            toValue: popupMenuLeft,
            useNativeDriver: true
          })
          .start()
      } else {
        this.popupMenuLeft.setValue(popupMenuLeft)
      }
    }

    if (popupMenuPositionY != this.tt.popupMenuPositionY || !initialized) {
      this.popupMenuPositionY = popupMenuPositionY

      if (mode == "dimensionsChange" && this.initialized) {
        Animated
          .timing(this.popupMenuTop, {
            duration: 200,
            easing: Easing.inOut(Easing.linear),
            toValue: popupMenuTop,
            useNativeDriver: true
          })
          .start()
      } else {
        this.popupMenuTop.setValue(popupMenuTop)
      }
    }
  }

  onHoverMenuLayout = ({nativeEvent}) => {
    const {height, width} = nativeEvent.layout

    this.hoverMenuHeight = height
    this.hoverMenuWidth = width
    this.positionHoverMenu()

    if (!this.tt.initialized) {
      this.initialized = true
      this.setState({visibility: "visible"})
    }
  }

  onMouseMove = (e) => {
    this.clientX = e.clientX + document.documentElement.scrollLeft
    this.clientY = e.clientY + document.documentElement.scrollTop
  }

  onPress = () => {
    if (this.s.showMenu) {
      this.closeMenu()
    } else {
      const {clientX, clientY, hoverMenuWidth, windowWidth} = this.tt
      const furthestX = clientX + hoverMenuWidth
      let popupMenuLeft, popupMenuPositionX

      if (furthestX >= windowWidth) {
        popupMenuLeft = clientX - hoverMenuWidth
        popupMenuPositionX = "left"
      } else {
        popupMenuLeft = clientX
        popupMenuPositionX = "right"
      }

      this.popupMenuClientX = clientX
      this.popupMenuClientY = clientY
      this.popupMenuLeftValue = popupMenuLeft
      this.popupMenuTopValue = clientY
      this.popupMenuLeft.setValue(0)
      this.popupMenuTop.setValue(0)
      this.popupMenuPositionX = popupMenuPositionX
      this.popupMenuPositionY = "below"
      this.setState({showMenu: true})
    }
  }

  onDimensionsChange = ({window}) => {
    this.windowWidth = window.width
    this.windowHeight = window.height

    if (this.initialized && this.tt.hoverMenuWidth && this.tt.hoverMenuHeight) {
      this.positionHoverMenu("dimensionsChange")
    }
  }
}

const ChalkyLayoutHoverMenu = memo(shapeComponent(ChalkyLayoutHoverMenuInner))

export {ChalkyLayoutHoverMenu as HoverMenu, ChalkyLayoutHoverMenuItem as HoverMenuItem}
